import React, { useEffect, useState, useLayoutEffect } from 'react';
import { CaseStudyHeader } from '../components/CaseStudyHeader';
import { CaseStudyMobileHeader } from '../components/CaseStudyMobileHeader';
import { Footer } from '../components/Footer';
import { CustomCursor } from '../components/CustomCursor';
import { CaseStudyNavigation } from '../components/CaseStudyNavigation';
import { ChatDrawer } from '../components/ChatDrawer';
import { FloatingButtons } from '../components/FloatingButtons';
import { Link, useNavigate } from 'react-router-dom';
import { usePortfolio } from '../context/PortfolioContext';
import { useCaseStudyNavigation } from '../hooks/useCaseStudyNavigation';
import { ArrowLeft, ArrowRight, ArrowDown, Image, CheckCircle, Users, Database, Rocket, Target, ChatCircle, NavigationArrow, Stack, Clock, TrendUp, Lightbulb, Gear, Compass } from '@phosphor-icons/react';
import { useScrollAnimation } from '../utils/AnimationObserver';
import '../styles/animations.css';
import { LightboxImage } from '../components/LightboxImage';
interface CaseStudyCyberSecurityProps {
  isOverlay?: boolean;
}
export function CaseStudyCyberSecurity({
  isOverlay = false
}: CaseStudyCyberSecurityProps) {
  const {
    viewPreference,
    isChatDrawerOpen,
    navigatedFromChat
  } = usePortfolio();
  const navigate = useNavigate();
  // Check if user came from chat OR home page
  const cameFromChat = sessionStorage.getItem('cameFromChat') === 'true';
  const cameFromHome = sessionStorage.getItem('navigatedFromHome') === 'true';
  const showBackButton = cameFromChat || cameFromHome;
  const {
    openChatDrawer,
    previousPage,
    setPreviousPage
  } = usePortfolio();
  // Handle back navigation to home
  const handleBackToHome = () => {
    // Clear navigation flags
    sessionStorage.removeItem('cameFromChat');
    sessionStorage.removeItem('navigatedFromHome');
    // Reset scroll position IMMEDIATELY before navigation
    window.scrollTo(0, 0);
    const homePath = viewPreference === 'ai' ? '/portfolio/ai' : '/portfolio/traditional';
    navigate(homePath);
  };
  // Handle chat state when navigating to this case study
  useCaseStudyNavigation();
  // Determine home path based on view preference
  const homePath = viewPreference === 'ai' ? '/portfolio/ai' : '/portfolio/traditional';
  // Initialize scroll animations
  useScrollAnimation();
  // Ensure page starts at top - CRITICAL: Run before any rendering
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);
  // Active section state for sticky navigation
  const [activeSection, setActiveSection] = useState('overview');
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    };
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    // Observe all sections
    const sections = ['overview', 'situation', 'task', 'action', 'results'];
    sections.forEach(sectionId => {
      const element = document.getElementById(sectionId);
      if (element) {
        observer.observe(element);
      }
    });
    return () => {
      sections.forEach(sectionId => {
        const element = document.getElementById(sectionId);
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, []);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 128; // Account for sticky header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  // Dummy handler for ChatDrawer (navigation handled inside ChatDrawer)
  const handleCaseStudyClick = (slug: string) => {
    console.log('Case study navigation handled by ChatDrawer:', slug);
  };
  const navItems = [{
    id: 'overview',
    label: 'Overview'
  }, {
    id: 'situation',
    label: 'Challenge'
  }, {
    id: 'task',
    label: 'Objectives'
  }, {
    id: 'action',
    label: 'Approach'
  }, {
    id: 'results',
    label: 'Outcome'
  }];
  return <>
      {!isOverlay && <CustomCursor data-id="element-776" />}

      <div className="flex min-h-screen bg-white text-black font-switzer" data-id="element-777">
        <div className="flex-1 min-w-0 flex flex-col relative" data-id="element-778">
          {!isOverlay && <CaseStudyHeader data-id="element-779" />}
          {!isOverlay && <CaseStudyMobileHeader data-id="element-780" />}

          {/* Hero Section - Clean White Background */}
          <section className="pt-32 pb-12 px-4 sm:px-8 md:px-12 lg:px-24 max-w-[1400px] mx-auto w-full" data-id="element-781">
            <div className="max-w-4xl" data-id="element-782">
              {/* Back to Portfolio button - always visible */}
              {!isOverlay && <button onClick={handleBackToHome} className="flex items-center gap-2 text-gray-600 hover:text-[#016768] transition-colors mb-6 group" aria-label="Back to portfolio" style={{
              marginBottom: '28px'
            }} data-id="element-783">
                  <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" data-id="element-784" />
                  <span className="text-sm font-medium" data-id="element-785">Back to Portfolio</span>
                </button>}

              <h1 className="text-4xl sm:text-5xl font-light tracking-tight mb-8 text-[#016768] leading-[1.1]" data-id="element-786">
                Cybersecurity Platform Redesign
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl font-light" data-id="element-787">
                Redesigning a complex cybersecurity platform to compete with
                market leaders while maintaining technical depth for enterprise
                security teams.
              </p>
            </div>
          </section>

          {/* Full Width Hero Image */}
          <div className="w-full h-[60vh] sm:h-[80vh] overflow-hidden mb-24" data-id="element-788">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/odCcwZqxVnrHSvVy5xeJrh/unisys_thumb.png" alt="Unisys Stealth Hero" className="w-full h-full object-cover object-center" data-id="element-789" />
          </div>

          {/* Main Content Grid */}
          <div className="max-w-[1400px] mx-auto w-full px-4 sm:px-8 md:px-12 lg:px-24 pb-32" data-id="element-790">
            <div className="lg:grid lg:grid-cols-12 gap-12" data-id="element-791">
              {/* Sticky Navigation Sidebar */}
              <div className="hidden lg:block lg:col-span-3" data-id="element-792">
                <div className="sticky top-32 space-y-6" data-id="element-793">
                  {navItems.map(item => <button key={item.id} onClick={() => scrollToSection(item.id)} className={`block text-sm tracking-wide transition-colors duration-300 ${activeSection === item.id ? 'text-[#016768] font-medium border-l-2 border-[#016768] pl-4' : 'text-gray-400 hover:text-gray-600 pl-4 border-l-2 border-transparent'}`} data-id="element-794">
                      {item.label}
                    </button>)}
                </div>
              </div>

              {/* Content Column */}
              <div className="lg:col-span-9 space-y-32" data-id="element-795">
                {/* Overview Section */}
                <section id="overview" className="scroll-mt-32 -mx-4 sm:-mx-8 md:-mx-12 lg:-mx-24 px-4 sm:px-8 md:px-12 lg:px-24 py-16 bg-gray-50" data-id="element-796">
                  <div className="grid md:grid-cols-3 gap-16 mb-16" data-id="element-797">
                    <div data-id="element-798">
                      <h3 className="text-sm font-medium text-[#016768] uppercase tracking-wider mb-2" data-id="element-799">
                        Role
                      </h3>
                      <p className="text-lg text-slate-700" data-id="element-800">Lead UX Designer</p>
                    </div>
                    <div data-id="element-801">
                      <h3 className="text-sm font-medium text-[#016768] uppercase tracking-wider mb-2" data-id="element-802">
                        Project Duration
                      </h3>
                      <p className="text-lg text-slate-700" data-id="element-803">6 Months</p>
                    </div>
                  </div>

                  <h2 className="text-3xl font-light mb-8 text-[#016768]" data-id="element-804">
                    Overview
                  </h2>
                  <p className="text-lg text-slate-600 leading-relaxed mb-12 max-w-3xl" data-id="element-805">
                    Led the redesign of Unisys Stealth (v5 to v6), transforming
                    a complex cybersecurity platform to compete with market
                    leaders like Illumio. Reduced 200 pages of technical
                    documentation into intuitive workflows while maintaining the
                    sophisticated functionality security professionals require.
                  </p>

                  <div className="bg-white rounded-2xl p-8 md:p-12 mb-12" data-id="element-806">
                    <h3 className="text-xl font-medium mb-8 text-[#016768]" data-id="element-807">
                      Impact
                    </h3>
                    <div className="space-y-4" data-id="element-808">
                      {['Redesigned complex cybersecurity workflows for improved usability while maintaining technical functionality', 'Developed comprehensive design system with updated components aligned with modern UI standards', 'Influenced strategic business decisions through competitive analysis and industry research', 'Positioned client competitively against Illumio through strategic design recommendations'].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-809">
                          <CheckCircle className="w-5 h-5 text-[#016768] mt-1 flex-shrink-0" data-id="element-810" />
                          <span className="text-slate-700 text-lg" data-id="element-811">{item}</span>
                        </div>)}
                    </div>
                  </div>

                  <div className="mb-16" data-id="element-812">
                    <LightboxImage src="https://cdn.magicpatterns.com/uploads/8Z9WqxDbqCWDcRCMWd5zyF/Unisys.png" alt="Unisys Stealth dashboard interface" label="Stealth Dashboard" data-id="element-813" />
                  </div>
                </section>

                {/* Situation Section */}
                <section id="situation" className="scroll-mt-32" data-id="element-814">
                  <h2 className="text-3xl font-light mb-12 text-[#016768]" data-id="element-815">
                    The Challenge
                  </h2>

                  <div className="mb-12 bg-gray-50 rounded-2xl p-8" data-id="element-816">
                    <h3 className="text-xl font-medium mb-6 text-[#016768]" data-id="element-817">
                      The User
                    </h3>
                    <p className="text-lg text-slate-700 leading-relaxed mb-6" data-id="element-818">
                      Security professionals working{' '}
                      <span className="font-semibold text-slate-800" data-id="element-819">
                        8-hour shifts in low-lit environments
                      </span>
                      , constantly switching between command-line tools in dark
                      mode and Stealth's bright, cluttered interface.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-820">
                      <div data-id="element-821">
                        <h4 className="font-medium mb-2 text-[#016768] text-base" data-id="element-822">
                          Primary Needs
                        </h4>
                        <p className="text-slate-600" data-id="element-823">
                          Reduce eye strain, intuitive layout with minimal
                          jargon, quick export capabilities
                        </p>
                      </div>
                      <div data-id="element-824">
                        <h4 className="font-medium mb-2 text-[#016768] text-base" data-id="element-825">
                          Work Environment
                        </h4>
                        <p className="text-slate-600" data-id="element-826">
                          Dark mode command line alongside platform,
                          high-pressure security decisions
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Problem Statement */}
                  <div className="mb-12 border-l-2 border-[#016768] pl-8 py-4" data-id="element-827">
                    <h3 className="text-xs font-bold text-[#016768] uppercase tracking-wider mb-6" data-id="element-828">
                      Problem Statement
                    </h3>
                    <p className="text-2xl font-light leading-relaxed text-slate-900 mb-0" data-id="element-829">
                      Security professionals needed to quickly analyze threats
                      and configure micro-segmentation policies, but Stealth's
                      complex interface with technical jargon, poor visual
                      hierarchy, and lack of intuitive workflows{' '}
                      <span className="font-medium text-[#016768]" data-id="element-830">
                        increased cognitive load and slowed critical security
                        decisions.
                      </span>
                    </p>
                  </div>

                  <div className="mb-12" data-id="element-831">
                    <LightboxImage src="https://cdn.magicpatterns.com/uploads/asQNUakddLpBCPbCr2XyP6/Stealth-image-1.jpg" alt="Unisys Stealth v5 interface" label="Stealth v5 Interface" data-id="element-832" />
                  </div>
                </section>

                {/* Task Section */}
                <section id="task" className="scroll-mt-32" data-id="element-833">
                  <h2 className="text-3xl font-light mb-12 text-[#016768]" data-id="element-834">
                    Objectives
                  </h2>

                  <p className="text-lg text-slate-600 leading-relaxed mb-12 max-w-3xl" data-id="element-835">
                    Design a platform that matches Illumio's usability without
                    sacrificing technical depth. Balance the needs of
                    specialists who require granular control with the urgency of
                    working in high-pressure, low-light environments. Create an
                    experience that wins sales meetings on first impression
                    while maintaining sophisticated functionality.
                  </p>

                  <div className="bg-gray-50 rounded-2xl p-8 mb-12" data-id="element-836">
                    <h3 className="text-xl font-medium mb-6 text-[#016768]" data-id="element-837">
                      Project Goals
                    </h3>
                    <div className="space-y-4" data-id="element-838">
                      {['Competitive positioning: Match or exceed market leaders while maintaining technical superiority', 'Specialized user needs: Design for low-light environments with complex technical requirements', 'Sales enablement: Create visually impressive platform that demonstrates value in first meetings', 'Technical balance: Simplify micro-segmentation workflows without sacrificing granular control'].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-839">
                          <div className="w-6 h-6 rounded-full border-2 border-[#016768] flex items-center justify-center text-[#016768] mt-0.5 flex-shrink-0" data-id="element-840">
                            <span className="text-xs font-medium" data-id="element-841">
                              {idx + 1}
                            </span>
                          </div>
                          <span className="text-slate-700 text-lg" data-id="element-842">{item}</span>
                        </div>)}
                    </div>
                  </div>
                </section>

                {/* Action Section */}
                <section id="action" className="scroll-mt-32" data-id="element-843">
                  <h2 className="text-3xl font-light mb-12 text-[#016768]" data-id="element-844">
                    Approach
                  </h2>

                  <div className="space-y-24" data-id="element-845">
                    {/* Execution - Research */}
                    <div data-id="element-846">
                      <div className="flex items-center gap-4 mb-6" data-id="element-847">
                        <span className="text-4xl font-light text-slate-300" data-id="element-848">
                          01
                        </span>
                        <h3 className="text-2xl font-medium text-[#016768]" data-id="element-849">
                          Heuristic Evaluation
                        </h3>
                      </div>

                      <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-3xl" data-id="element-850">
                        Evaluated the entire platform against established
                        usability principles, revealing critical gaps: poor
                        visual hierarchy, technical jargon overload, and
                        workflows that increased cognitive load during
                        high-pressure security decisions.
                      </p>

                      <div className="mb-8" data-id="element-851">
                        <LightboxImage src="https://cdn.magicpatterns.com/uploads/gWvkJF6TBtxRD48ugYuk6r/Heuristic_Evaluation_2_1.png" alt="Heuristic Evaluation of Unisys Stealth Aware 5.0" label="Heuristic Evaluation" data-id="element-852" />
                      </div>

                      <div className="bg-gray-50 rounded-2xl p-8" data-id="element-853">
                        <h4 className="text-xl font-medium mb-6 text-[#016768]" data-id="element-854">
                          Key Insights
                        </h4>
                        <div className="space-y-4" data-id="element-855">
                          <div data-id="element-856">
                            <h5 className="font-medium text-slate-900 mb-2" data-id="element-857">
                              User Needs
                            </h5>
                            <p className="text-slate-700 leading-relaxed" data-id="element-858">
                              Users needed{' '}
                              <span className="font-semibold text-slate-800" data-id="element-859">
                                dark mode, minimal jargon, and quick export
                                capabilities
                              </span>
                              , none of which v5 provided.
                            </p>
                          </div>
                          <div data-id="element-860">
                            <h5 className="font-medium text-slate-900 mb-2" data-id="element-861">
                              Visual Hierarchy
                            </h5>
                            <p className="text-slate-700 leading-relaxed" data-id="element-862">
                              Improved information architecture with clear
                              visual hierarchy, grouping related elements
                              through proximity to reduce cognitive load.
                            </p>
                          </div>
                          <div data-id="element-863">
                            <h5 className="font-medium text-slate-900 mb-2" data-id="element-864">
                              Recognition Over Recall
                            </h5>
                            <p className="text-slate-700 leading-relaxed" data-id="element-865">
                              Redesigned affinity level controls to show product
                              detail at different levels with clear, descriptive
                              labels instead of requiring users to remember
                              abstract settings.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Execution - Competitive Analysis */}
                    <div data-id="element-866">
                      <div className="flex items-center gap-4 mb-6" data-id="element-867">
                        <span className="text-4xl font-light text-slate-300" data-id="element-868">
                          02
                        </span>
                        <h3 className="text-2xl font-medium text-[#016768]" data-id="element-869">
                          Competitive Analysis
                        </h3>
                      </div>

                      <p className="text-lg text-slate-600 leading-relaxed mb-6 max-w-3xl" data-id="element-870">
                        Analyzed market leaders and presented findings to
                        leadership. Competitors simplified with 80-page
                        summaries while we had 200 pages. Their "caveman could
                        do it" messaging resonated, ours didn't.
                      </p>

                      <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-3xl" data-id="element-871">
                        Each competitor presented threat data differently.{' '}
                        <span className="font-semibold text-slate-800" data-id="element-872">
                          Illumio used motion to guide user attention
                        </span>{' '}
                        through their interactive dashboard of nodes and
                        connections, clearly showing affordances for what users
                        could do with each element.{' '}
                        <span className="font-semibold text-slate-800" data-id="element-873">
                          CrowdStrike paired consistent UI with purposeful
                          animation.
                        </span>{' '}
                        Meanwhile, current industry styles suffered from
                        accessibility issues with poor text contrast.
                      </p>

                      <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm mb-8" data-id="element-874">
                        <div style={{
                        padding: '56.25% 0 0 0',
                        position: 'relative'
                      }} data-id="element-875">
                          <iframe src="https://player.vimeo.com/video/1144651751?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%'
                        }} title="Cyberoverview" data-id="element-876"></iframe>
                        </div>
                        <p className="text-sm text-gray-500 p-4 text-center bg-gray-50 border-t border-gray-100" data-id="element-877">
                          Competitor analysis: Illumio's interactive dashboard
                          with motion-guided interactions
                        </p>
                      </div>

                      <div className="bg-gray-50 rounded-2xl p-8" data-id="element-878">
                        <h4 className="text-xl font-medium mb-4 text-[#016768]" data-id="element-879">
                          Strategic Recommendation
                        </h4>
                        <p className="text-lg text-slate-700 leading-relaxed mb-4" data-id="element-880">
                          Partner with CrowdStrike. Their product fills
                          functional gaps while their consistent UI and
                          animation patterns align with best practices.{' '}
                          <span className="font-semibold text-slate-800" data-id="element-881">
                            This partnership would make the combined offering
                            highly competitive on both functionality and form.
                          </span>
                        </p>
                        <p className="text-lg text-slate-700 leading-relaxed" data-id="element-882">
                          Parallel path: Study interaction design and motion
                          principles to write requirements that visually aid
                          this complex experience, helping users understand
                          product capabilities while updating the UI for future
                          interaction improvements.{' '}
                          <span className="font-semibold text-slate-800" data-id="element-883">
                            Partnership later implemented, validating the
                            strategic direction.
                          </span>
                        </p>
                      </div>
                    </div>

                    {/* Execution - Design & Development */}
                    <div data-id="element-884">
                      <div className="flex items-center gap-4 mb-6" data-id="element-885">
                        <span className="text-4xl font-light text-slate-300" data-id="element-886">
                          03
                        </span>
                        <h3 className="text-2xl font-medium text-[#016768]" data-id="element-887">
                          Design Solutions
                        </h3>
                      </div>

                      <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-3xl" data-id="element-888">
                        Collaborated with engineering teams to understand
                        technical constraints and translate user needs into
                        actionable design solutions. Created artifacts that
                        became validation tools throughout the project.
                      </p>

                      <div className="mb-12" data-id="element-889">
                        <h4 className="text-xl font-medium mb-4 text-[#016768]" data-id="element-890">
                          Journey Map & User Research
                        </h4>
                        <p className="text-lg text-slate-600 leading-relaxed mb-6 max-w-3xl" data-id="element-891">
                          Created comprehensive journey map informed by{' '}
                          <span className="font-semibold text-slate-800" data-id="element-892">
                            40 user interviews across different security
                            professional archetypes.
                          </span>{' '}
                          This service blueprint hybrid became the source of
                          truth for validating design decisions against actual
                          user workflows and technical constraints identified
                          through engineering collaboration.
                        </p>
                        <LightboxImage src="https://cdn.magicpatterns.com/uploads/iyfZFxYsEpmMRL3FTtStEF/Journey_Map_Unisys.png" alt="Unisys Stealth UI Journey Map" label="User Journey Map" data-id="element-893" />
                      </div>

                      <div className="mb-12" data-id="element-894">
                        <h4 className="text-xl font-medium mb-4 text-[#016768]" data-id="element-895">
                          Threat Detection Dashboard
                        </h4>
                        <p className="text-lg text-slate-600 leading-relaxed mb-6 max-w-3xl" data-id="element-896">
                          Collaborated with development team on threat detection
                          dashboard integration with Grafana.{' '}
                          <span className="font-semibold text-slate-800" data-id="element-897">
                            Conducted library research, evaluated tech stacks,
                            and designed information architecture
                          </span>{' '}
                          within tight technical constraints. Senior designers
                          provided final execution support.
                        </p>
                        <LightboxImage src="https://cdn.magicpatterns.com/uploads/tD1r8ynrxrAuLtn92ee2hj/Threatdashboard.png" alt="Unisys Stealth Security Dashboard" label="Threat Dashboard Design" data-id="element-898" />
                      </div>
                    </div>
                  </div>
                </section>

                {/* Results Section */}
                <section id="results" className="scroll-mt-32" data-id="element-899">
                  <h2 className="text-3xl font-light mb-12 text-[#016768]" data-id="element-900">
                    Outcome
                  </h2>

                  <div className="mb-12" data-id="element-901">
                    <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm mb-8" data-id="element-902">
                      <div style={{
                      padding: '56.25% 0 0 0',
                      position: 'relative'
                    }} data-id="element-903">
                        <iframe src="https://player.vimeo.com/video/476985592?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%'
                      }} title="Stealth(core)™ Demonstration" data-id="element-904"></iframe>
                      </div>
                      <p className="text-sm text-gray-500 p-4 text-center bg-gray-50 border-t border-gray-100" data-id="element-905">
                        Unisys Stealth Design Showcase - Final Implementation
                      </p>
                    </div>
                  </div>

                  <div className="bg-[#016768] text-white p-12 rounded-2xl mb-16" data-id="element-906">
                    <h3 className="text-2xl font-light mb-8" data-id="element-907">Impact</h3>

                    <div className="space-y-6" data-id="element-908">
                      <div className="flex items-start gap-4" data-id="element-909">
                        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-910">
                          <CheckCircle className="w-5 h-5 text-white" data-id="element-911" />
                        </div>
                        <div data-id="element-912">
                          <h4 className="text-lg font-medium mb-2" data-id="element-913">
                            Competitive Positioning
                          </h4>
                          <p className="text-white/90 leading-relaxed" data-id="element-914">
                            Redesign positioned client competitively against
                            Illumio with simplified workflows that maintained
                            technical depth
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4" data-id="element-915">
                        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-916">
                          <CheckCircle className="w-5 h-5 text-white" data-id="element-917" />
                        </div>
                        <div data-id="element-918">
                          <h4 className="text-lg font-medium mb-2" data-id="element-919">
                            Strategic Validation
                          </h4>
                          <p className="text-white/90 leading-relaxed" data-id="element-920">
                            CrowdStrike partnership recommendation (proposed
                            during design phase) was later implemented by client
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4" data-id="element-921">
                        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-922">
                          <CheckCircle className="w-5 h-5 text-white" data-id="element-923" />
                        </div>
                        <div data-id="element-924">
                          <h4 className="text-lg font-medium mb-2" data-id="element-925">
                            Design System Adoption
                          </h4>
                          <p className="text-white/90 leading-relaxed" data-id="element-926">
                            Comprehensive design system and component library
                            adopted for v6 launch
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4" data-id="element-927">
                        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-928">
                          <CheckCircle className="w-5 h-5 text-white" data-id="element-929" />
                        </div>
                        <div data-id="element-930">
                          <h4 className="text-lg font-medium mb-2" data-id="element-931">
                            Stakeholder Response
                          </h4>
                          <p className="text-white/90 leading-relaxed" data-id="element-932">
                            "This is exactly what we needed to win sales
                            meetings" - Client Business Lead
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>

          {!isOverlay && <CaseStudyNavigation homePath={homePath} previousCase={{
          category: 'Case Studies',
          title: 'U.S. Bank',
          slug: 'us-bank'
        }} nextCase={{
          category: 'Case Studies',
          title: 'AI Comparison',
          slug: 'ai-comparison'
        }} data-id="element-933" />}

          {!isOverlay && <Footer data-id="element-934" />}
        </div>

        {!isOverlay && <ChatDrawer onCaseStudyClick={handleCaseStudyClick} data-id="element-935" />}
      </div>

      {!isOverlay && !isChatDrawerOpen && <FloatingButtons data-id="element-936" />}
    </>;
}